import * as React from "react";

/** Where a tab currently lives: in the strip, in the open overflow menu, or measured off-screen. */
export type TabLocation = "main" | "overflow" | "hidden";

/**
 * `inline` first, `portal` once every tab has been measured.
 *
 * Tabs must render in the document to have a width, but the overflow layout needs those widths
 * before it can decide what goes where. So the first pass renders them plainly, and only when
 * all of them report a real width does the list switch to moving portal hosts between slots.
 * Rendering into portals from the start would measure zeros forever.
 */
export type TabRenderMode = "inline" | "portal";

export interface CollectionItem {
  id: string;
  value: string;
  element: HTMLElement | null;
  location?: TabLocation;
  order?: number;
}

export interface RenderedTab {
  host: HTMLElement;
  id: string;
  marker: HTMLElement | null;
  root: HTMLElement | null;
  trigger: HTMLElement | null;
  value: string;
  width: number;
}

export interface TabsContextValue {
  activeTab: React.MutableRefObject<{ value: string; id: string } | undefined>;
  getFirst: () => CollectionItem | null;
  getIndex: (id?: string) => number;
  getLast: () => CollectionItem | null;
  getNext: (id?: string) => CollectionItem | null;
  getPanelId: (value: string) => string | undefined;
  getPrevious: (id?: string) => CollectionItem | null;
  getRenderedTab: (value: string) => RenderedTab | undefined;
  getRenderedTabOrder: (value: string) => number;
  getTabId: (value: string) => string | undefined;
  item: (id?: string) => CollectionItem | null;
  itemAt: (index: number) => CollectionItem | null;
  menuOpen: boolean;
  registerBootstrapTab: (value: string) => () => void;
  registerPanel: (id: string, value: string) => () => void;
  registerRenderedTab: (tab: RenderedTab) => () => void;
  registerTab: (item: CollectionItem) => () => void;
  renderMode: TabRenderMode;
  renderedTabs: RenderedTab[];
  selected: string | undefined;
  selectionFromOverflowValueRef: React.MutableRefObject<string | null>;
  setBootstrapOverflowReady: (ready: boolean) => void;
  setBootstrapTabReady: (value: string, ready: boolean) => void;
  setMenuOpen: (open: boolean) => void;
  setSelected: (event: React.SyntheticEvent | null, value: string, source?: "main" | "overflow") => void;
  sortItems: () => void;
  updateRenderedTab: (value: string, updates: Partial<RenderedTab>) => void;
  updateTab: (id: string, updates: Partial<CollectionItem>) => void;
}

const noop = () => undefined;
const noopCleanup = () => () => undefined;

export const TabsContext = React.createContext<TabsContextValue>({
  activeTab: { current: undefined },
  getFirst: () => null,
  getIndex: () => -1,
  getLast: () => null,
  getNext: () => null,
  getPanelId: () => undefined,
  getPrevious: () => null,
  getRenderedTab: () => undefined,
  getRenderedTabOrder: () => -1,
  getTabId: () => undefined,
  item: () => null,
  itemAt: () => null,
  menuOpen: false,
  registerBootstrapTab: noopCleanup,
  registerPanel: noopCleanup,
  registerRenderedTab: noopCleanup,
  registerTab: noopCleanup,
  renderMode: "inline",
  renderedTabs: [],
  selected: undefined,
  selectionFromOverflowValueRef: { current: null },
  setBootstrapOverflowReady: noop,
  setBootstrapTabReady: noop,
  setMenuOpen: noop,
  setSelected: noop,
  sortItems: noop,
  updateRenderedTab: noop,
  updateTab: noop,
});

export function useTabs() {
  return React.useContext(TabsContext);
}

export interface TabContextValue {
  actions: string[];
  disabled: boolean;
  focused: boolean;
  registerAction: (id: string) => () => void;
  selected: boolean;
  tabId?: string;
  value: string;
}

export const TabContext = React.createContext<TabContextValue>({
  actions: [],
  disabled: false,
  focused: false,
  registerAction: noopCleanup,
  selected: false,
  value: "",
});

export function useTab() {
  return React.useContext(TabContext);
}

export interface TabListLayoutContextValue {
  getLocation: (value: string) => TabLocation;
  moveOverflowFocus: (key: string, value: string) => boolean;
  overflowActiveValue: string | null;
  setOverflowActiveValue: (value: string | null) => void;
}

export const TabListLayoutContext = React.createContext<TabListLayoutContextValue | null>(null);

export function useTabListLayout() {
  return React.useContext(TabListLayoutContext);
}

export interface TabSlotRegistryValue {
  registerSlot: (slotId: string, element: HTMLElement | null) => void;
}

export const TabSlotRegistryContext = React.createContext<TabSlotRegistryValue | null>(null);

export function useTabSlotRegistry() {
  return React.useContext(TabSlotRegistryContext);
}
